import { useState } from "react";
import PublicLayout from "@/components/PublicLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { useConfig, whatsappHref, telHref } from "@/lib/config";

const CITIES = ["Noida", "Greater Noida", "Ghaziabad", "Gurugram", "Faridabad", "Delhi", "Other NCR", "Intercity"];
const BHKS = ["1 RK", "1 BHK", "2 BHK", "3 BHK", "4 BHK / Villa", "Office / Commercial"];

export default function ContactPage() {
  const cfg = useConfig();
  const [form, setForm] = useState({
    name: "", phone: "", email: "", from_city: "Noida", to_city: "Gurugram",
    bhk: "2 BHK", move_date: "", notes: "",
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v?.target?.value ?? v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !/^\d{10}$/.test(form.phone.replace(/\D/g, "").slice(-10))) {
      toast.error("Please enter a valid name and 10-digit phone");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, source: "contact_page" });
      toast.success("Thank you! Owner will WhatsApp/call you in 5 minutes.");
      setForm({ ...form, name: "", phone: "", email: "", notes: "" });
    } catch (err) {
      toast.error("Something went wrong — please WhatsApp us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <section className="bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] text-white py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold">Get Your Free Quote</h1>
          <p className="mt-3 text-slate-200 max-w-2xl">Form fill karo — 5 minute mein WhatsApp / call pe rate ready hoga. Zero spam.</p>
        </div>
      </section>

      <section className="py-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_400px] gap-8">
        <Card className="p-6 sm:p-8 border-2 border-orange-100">
          <h2 className="font-display text-2xl font-bold text-[#0F172A]">Free Quote Form</h2>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="contact-lead-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Your Name *</Label>
                <Input value={form.name} onChange={set("name")} className="mt-1" data-testid="contact-name-input" />
              </div>
              <div>
                <Label>Phone Number *</Label>
                <Input value={form.phone} onChange={set("phone")} placeholder="10-digit" className="mt-1" data-testid="contact-phone-input" />
              </div>
            </div>
            <div>
              <Label>Email (optional)</Label>
              <Input type="email" value={form.email} onChange={set("email")} className="mt-1" data-testid="contact-email-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Moving From *</Label>
                <Select value={form.from_city} onValueChange={set("from_city")}>
                  <SelectTrigger className="mt-1" data-testid="contact-from-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Moving To *</Label>
                <Select value={form.to_city} onValueChange={set("to_city")}>
                  <SelectTrigger className="mt-1" data-testid="contact-to-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Home Size *</Label>
                <Select value={form.bhk} onValueChange={set("bhk")}>
                  <SelectTrigger className="mt-1" data-testid="contact-bhk-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{BHKS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Move Date</Label>
                <Input type="date" value={form.move_date} onChange={set("move_date")} className="mt-1" data-testid="contact-date-input" />
              </div>
            </div>
            <div>
              <Label>Notes / Special items</Label>
              <Textarea rows={3} value={form.notes} onChange={set("notes")} placeholder="Piano, fridge, LED TV, valuable items..." className="mt-1" data-testid="contact-notes-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3 pt-2">
              <Button type="submit" disabled={loading} className="bg-[#F97316] hover:bg-[#EA580C] text-white" data-testid="contact-submit-button">
                {loading ? "Sending..." : "Get Free Quote"}
              </Button>
              <Button type="button" onClick={() => window.open(whatsappHref(cfg.whatsapp_number, `Hi! I want a quote.\nName: ${form.name}\nPhone: ${form.phone}\nFrom: ${form.from_city}\nTo: ${form.to_city}\nBHK: ${form.bhk}\nDate: ${form.move_date || "-"}`), "_blank")} className="bg-[#22C55E] hover:bg-[#16A34A] text-white" data-testid="contact-whatsapp-button">
                <MessageCircle className="w-4 h-4 mr-1" /> Chat on WhatsApp
              </Button>
            </div>
          </form>
        </Card>

        <div className="space-y-4">
          <Card className="p-6">
            <h3 className="font-display font-bold text-lg text-[#0F172A]">Talk To Us Now</h3>
            <div className="mt-4 space-y-3">
              <a href={telHref(cfg.contact_phone)} className="flex items-center gap-3 hover:text-[#F97316]" data-testid="contact-phone-link">
                <Phone className="w-5 h-5 text-[#F97316]" />
                <div>
                  <div className="text-xs text-slate-500">Call 24×7</div>
                  <div className="font-semibold">{cfg.contact_phone || "+91 99999 99999"}</div>
                </div>
              </a>
              <a href={whatsappHref(cfg.whatsapp_number, "Namaste! I want a shifting quote.")} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 hover:text-[#22C55E]" data-testid="contact-wa-link">
                <MessageCircle className="w-5 h-5 text-[#22C55E]" />
                <div>
                  <div className="text-xs text-slate-500">WhatsApp Business</div>
                  <div className="font-semibold">{cfg.whatsapp_number || "+91 99999 99999"}</div>
                </div>
              </a>
              <a href="mailto:mvpackersandmover@gmail.com" className="flex items-center gap-3 hover:text-[#1E3A8A]">
                <Mail className="w-5 h-5 text-[#1E3A8A]" />
                <div>
                  <div className="text-xs text-slate-500">Email</div>
                  <div className="font-semibold break-all">mvpackersandmover@gmail.com</div>
                </div>
              </a>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-500 mt-1 flex-none" />
                <div>
                  <div className="text-xs text-slate-500">Head Office</div>
                  <div className="font-semibold">Shop No. 5, Adarsh Market, Harola Main Road, Sector 5, Noida, UP 201301</div>
                  <div className="text-xs text-slate-500 mt-2">Branch Office</div>
                  <div className="font-semibold">Shop No. 1, Durga Market, Bhangel Main Road, Noida, UP 201304</div>
                </div>
              </div>
            </div>
          </Card>
          <Card className="p-6 bg-gradient-to-br from-orange-50 to-yellow-50 border-orange-200">
            <div className="text-xs font-semibold text-[#F97316] uppercase tracking-wider">🪔 Diwali Offer</div>
            <div className="font-display text-xl font-extrabold text-[#0F172A] mt-1">Flat 10% Off + Free Bubble Wrap</div>
            <div className="text-sm text-slate-600 mt-1">Use code <span className="font-bold">DIWALI10</span> — valid till 15 Nov 2026.</div>
          </Card>
        </div>
      </section>
    </PublicLayout>
  );
}
